import { useState } from "react";

import { Icon, IconVariant } from "@/DesignSystem";

import styles from "./Notice.module.scss";

import CheckFill from "@/DesignSystem/Atoms/Icons/Types/CheckCircle/check-circle-fill.svg";
import ChevronDown from "@/DesignSystem/Atoms/Icons/Types/ChevronDown/chevron-down-regular.svg";
import ChevronUp from "@/DesignSystem/Atoms/Icons/Types/ChevronUp/chevron-up-regular.svg";
import InfoFill from "@/DesignSystem/Atoms/Icons/Types/Info/info-fill.svg";
import WarningFill from "@/DesignSystem/Atoms/Icons/Types/WarningCircle/warning-circle-fill.svg";
import XFill from "@/DesignSystem/Atoms/Icons/Types/XCircle/x-circle-fill.svg";

interface INoticeProps {
    /** If true, the notice will be collapsed by default (only applies if collapsable is true). */
    defaultCollapsed?: boolean;
    /** If true, the notice can be collapsed/expanded by the user. */
    collapsable?: boolean;
    /** If true, the icon will not be displayed. */
    hideIcon?: boolean;
    /** The width of the notice in pixels. If not provided, it will be "fit-content". */
    width?: number;
    /** The type of notice: "info", "warning", "error", or "success". */
    type?: "info" | "warning" | "error" | "success";
    /** The title of the notice, displayed above the message. */
    title?: string;
    /** The message to display in the notice. */
    message?: string;
    /** The HTML ID to set on the notice component, if any. */
    id?: string;
}

const typeParamsMap = {
    error: {
        className: styles.error,
        colorVariant: "error",
        icon: <XFill />
    },
    info: {
        className: styles.info,
        colorVariant: "brand",
        icon: <InfoFill />
    },
    success: {
        className: styles.success,
        colorVariant: "success",
        icon: <CheckFill />
    },
    warning: {
        className: styles.warning,
        colorVariant: "warning",
        icon: <WarningFill />
    }
};

/**
 * A notice component that displays a message with an icon.
 * @param {INoticeProps} props - The properties for the notice component.
 * @param {string} [props.type] - The type of notice, can be "info", "warning", "error", or "success". Defaults to "info".
 * @param {React.ReactNode} [props.message] - The message to display in the notice.
 * @param {string | number} [props.width] - The width of the notice. If not provided, it will be "fit-content" for inline notices.
 * @param {boolean} [props.collapsable] - If true, the notice can be collapsed. Defaults to false.
 * @param {boolean} [props.defaultCollapsed] - If true, the notice will be collapsed by default. Defaults to true.
 * @param {boolean} [props.hideIcon] - If true, the icon will not be displayed. Defaults to false.
 * @param {string} [props.title] - The title of the notice. If provided, it will be displayed above the message.
 * @param {string} [props.id] - The HTML ID to set on the notice component, if any.
 * @returns {JSX.Element} The rendered notice component.
 * @example
 * <Notice type="warning" message="This is a warning message." />
 */
function Notice({
    defaultCollapsed = true,
    collapsable = false,
    hideIcon = false,
    type = "info",
    width,
    title,
    message,
    id
}: Readonly<INoticeProps>): JSX.Element {
    const [collapsed, setCollapsed] = useState(defaultCollapsed);

    if (!message && !title) return null;

    const params = typeParamsMap[type];

    const renderTitle = (): JSX.Element => {
        if (!title)
            return null;

        return (
            <div className={`${styles.title} ${collapsable && collapsed ? styles.singleLineEllipsis : ""}`}>
                {title}
            </div>
        );
    };

    const renderMessage = (): JSX.Element => {
        if (!collapsable) {
            return <div className={styles.message}>{message}</div>;
        }

        if (title) {
            return !collapsed ? <div className={styles.message}>{message}</div> : null;
        }
        else {
            return (
                <div className={`${styles.message} ${collapsed ? styles.singleLineEllipsis : ""}`}>
                    {message}
                </div>
            );
        }
    };

    return (
        <div
            id={id}
            className={`${styles.container} ${params.className}`}
            style={{
                width: width ?? "fit-content"
            }}
        >
            {collapsable && (
                <button
                    className={styles.collapsableIcon}
                    onClick={() => setCollapsed(!collapsed)}
                >
                    <Icon size="sm">
                        {collapsed ? <ChevronDown /> : <ChevronUp />}
                    </Icon>
                </button>
            )}

            {(!hideIcon && !collapsable) && (
                <Icon size="sm" colorVariant={params.colorVariant as IconVariant}>
                    {params.icon}
                </Icon>
            )}

            <div className={styles.content}>
                {renderTitle()}
                {renderMessage()}
            </div>
        </div>
    );
}

export { Notice };
